<template>
    <div class="hts-company-info">
        <hts-card>
            <div class="flex justify-center align-middle">
                <div class="h-[100px] max-w-[400px]">
                    <img
                        class="h-full w-auto rounded object-cover object-position"
                        :src="logoUrl"
                        @error="onLogoError" >
                </div>
            </div>
            <div class="flex justify-center">
                <hts-form-button
                    size="small"
                    @click="openUploadImage">
                    {{ $t('labels.changeLogo') }}
                </hts-form-button>
                <hts-form-upload-image
                    ref="uploadImage"
                    :label="$t('labels.chooseLogo')"
                    type="logo"
                    :stencil-size="{
                        width: 800,
                        height: 200
                    }"
                    :api="{
                        method: 'put',
                        url: '/company/change-logo'
                    }"
                    :success-label="$t('labels.changeLogoSuccess')"
                    @upload-success="uploadSuccessImage" />
            </div>

            <template
                v-if="group.isAdmin || permissions.includes('company_edit')"
                #actions>
                <hts-form-button @click="handleEditClick">
                    {{ $t('labels.edit') }}
                </hts-form-button>
            </template>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div>
                    <p class="hts-user__information--label">
                        {{ companyData.name }}
                    </p>
                    <p class="hts-user__information--description">
                        {{ companyData.address }}
                    </p>
                </div>

                <div>
                    <h3>
                        {{ $t('labels.contactEmail') }}
                    </h3>
                    <p class="mt-2">
                        {{ companyData.contactEmail }}
                    </p>
                </div>
                <div>
                    <h3>
                        {{ $t('labels.phone') }}
                    </h3>
                    <p
                        v-if="companyData && companyData.phone"
                        class="mt-2">
                        <span v-if="typeof companyData.phone === 'string'">
                            {{ companyData.phone }}
                        </span>
                        <span v-else>
                            {{ `(${companyData.phone?.code}) ${companyData.phone?.phone}` }}
                        </span>
                    </p>
                </div>

                <div>
                    <h3>
                        {{ $t('labels.website') }}
                    </h3>
                    <p class="mt-2">
                        {{ companyData.website }}
                    </p>
                </div>

                <div>
                    <h3>
                        {{ $t('labels.taxNumber') }}
                    </h3>
                    <p class="mt-2">
                        {{ companyData.taxNumber }}
                    </p>
                </div>

                <div>
                    <h3>
                        {{ $t('labels.fax') }}
                    </h3>
                    <p class="mt-2">
                        {{ companyData.fax }}
                    </p>
                </div>
            </div>
        </hts-card>
        <edit-company
            ref="edit"
            :company-info="companyData"
            @update="handleUpdated" />
        <input
            ref="uploadImg"
            class="hidden"
            type="file"
            @change="onFileSelected" >
    </div>
</template>

<script setup lang="ts">
import editCompany from './ignore/edit-company.vue'
import companyPageTabs from '@/data/company/tabs.json'

export interface ICompany {
  name: string
  address: string
  website: string
  logoUrl: string
  taxNumber: string
  phone: {
    countryCode: string
    phone: string
    code: string
  }
  fax: string
  contactEmail: string
}

const store = $store()
const group = store.system.getGroup
const permissions = ref<Array<String>>([])
onMounted(() => {
  permissions.value = group.permissions
})
const route = useRoute()
// onBeforeMount(() => {
//   route.meta.pageTabs = companyPageTabs
// })
const uploadImage = ref()

const openUploadImage = () => {
  if (uploadImage.value) {
    uploadImage.value.open()
  }
}

const defaultLogoUrl = '/assets/logo.png'
const logo = computed(() => {
  return store.userConfig.getLogo
})

const logoUrl = computed(() => {
  return logo.value || defaultLogoUrl
})

const onLogoError = (event: Event) => {
  const target = event.target as HTMLImageElement
  target.style.display = 'none'
}

const loading = ref(true)
const runtimeConfig = useRuntimeConfig().public
const uploadImg = ref<HTMLElement>()
const edit = ref<any>(null)
const loaded = ref(false)
const companyData = ref<ICompany>({
  name: '',
  address: '',
  website: '',
  logoUrl: '',
  taxNumber: '',
  phone: {
    countryCode: '',
    phone: '',
    code: ''
  },
  fax: '',
  contactEmail: ''
})

const handleEditClick = () => {
  edit.value && edit.value.show(companyData.value)
}

const handleUpdated = async () => {
  await getCompanyInfo()
}

const getCompanyInfo = async () => {
  loading.value = true
  const company = await $api($url.company.getHoldingCompany)
  const data: any = $lodash.get(company, 'data.value')
  const error: any = $lodash.get(company, 'error')

  if (!data.success) {
    const msgError = $lodash.get(error, 'value.data.errors.0', 'Không thể kết nối tới máy chủ')
    $toast().error(msgError)
  } else {
    companyData.value = data.data
  }
  loading.value = false
}

const onUpdateAvatar = () => {
  uploadImg.value?.click()
}

const onFileSelected = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const extensionArr: string[] = ['image/jpg', 'image/jpeg', 'image/png', 'image/svg']
  if (target.files && target.files.length > 0) {
    const file: File = target.files[0]
    if (file.size > runtimeConfig.maxFileSize) {
      return $toast().error($t('labels.validSizeImage'))
    }
    if (!extensionArr.includes(file.type)) {
      return $toast().error($t('labels.validTypeFile'))
    }
    const fb = new FormData()
    fb.append('image', file)
    const result = await $api($url.settings.uploadCompanyLogo, { body: fb })
    const data: any = $lodash.get(result, 'data.value')
    if (data.success) {
      companyData.value.logoUrl = URL.createObjectURL(file)
      return $toast().success($t('labels.updateSuccess'))
    }
  }
}

const uploadSuccessImage = (data: { [key: string]: string }) => {
  store.userConfig.setLogo(data.logo)
}

onBeforeMount(async () => {
  await getCompanyInfo()
  if (!companyData.value.logoUrl) {
    companyData.value.logoUrl = '@/public/assets/logo.png'
  }
})
</script>
